import type { VolumeSettings } from "../game/types";

type SfxName =
  | "throw"
  | "pop"
  | "goldPop"
  | "life"
  | "reload"
  | "powerStart"
  | "powerEnd"
  | "gameOver"
  | "gameOverRecord"
  | "button"
  | "miss";

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private birdTimer = 0;
  private step = 0;
  private nextNote = 0;
  private musicOn = false;
  private waveSource: AudioBufferSourceNode | null = null;
  private waveLfo: OscillatorNode | null = null;
  private pad: OscillatorNode | null = null;
  private padGain: GainNode | null = null;
  private unlocked = false;
  private volumes: VolumeSettings = { music: 0.55, ambient: 0.4, sfx: 0.7 };
  private reduceMotion = false;

  setVolumes(volumes: VolumeSettings): void {
    this.volumes = volumes;
    this.applyGains();
  }

  setReduceMotion(value: boolean): void {
    this.reduceMotion = value;
  }

  async unlock(): Promise<void> {
    if (this.unlocked) {
      await this.ctx?.resume().catch(() => undefined);
      return;
    }
    try {
      const ctx = new AudioContext();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.musicGain = ctx.createGain();
      this.ambientGain = ctx.createGain();
      this.sfxGain = ctx.createGain();
      this.musicGain.connect(this.master);
      this.ambientGain.connect(this.master);
      this.sfxGain.connect(this.master);
      this.master.connect(ctx.destination);
      this.applyGains();
      this.startWaves(ctx);
      this.startPad(ctx);
      this.unlocked = true;
      await Promise.race([
        ctx.resume().catch(() => undefined),
        new Promise((resolve) => window.setTimeout(resolve, 400)),
      ]);
    } catch {
      this.unlocked = false;
    }
  }

  enterPlay(): void {
    this.musicOn = true;
    if (this.ctx) this.nextNote = this.ctx.currentTime;
    void this.ctx?.resume();
    this.setPadLevel(0.04);
  }

  enterPause(): void {
    this.musicOn = false;
    this.setPadLevel(0.012);
  }

  enterMenu(): void {
    this.musicOn = false;
    this.setPadLevel(0);
  }

  dispose(): void {
    this.musicOn = false;
    try {
      this.pad?.stop();
      this.waveLfo?.stop();
      this.waveSource?.stop();
    } catch {
      /* already stopped */
    }
    void this.ctx?.close();
    this.ctx = null;
    this.unlocked = false;
  }

  update(dt: number): void {
    if (!this.ctx || !this.unlocked) return;
    if (this.ctx.state !== "running") return;
    this.scheduleMusic();
    this.birdTimer -= dt;
    if (this.birdTimer <= 0) {
      this.chirp();
      this.birdTimer = 2.4 + Math.random() * 3.2;
    }
  }

  play(name: SfxName): void {
    if (!this.ctx || !this.unlocked) return;
    switch (name) {
      case "throw":
        this.sweep(420, 180, 0.16, "triangle", 0.12);
        break;
      case "pop":
        this.noiseBurst(0.12, 0.2, 900);
        this.tone(520, 0.09, 0.08);
        break;
      case "goldPop":
        this.arpeggio([659, 784, 988, 1174], 0.08, 0.09);
        this.noiseBurst(0.18, 0.16, 1400);
        break;
      case "life":
        this.sweep(320, 90, 0.28, "sine", 0.16);
        break;
      case "reload":
        this.arpeggio([392, 523, 659], 0.07, 0.07);
        break;
      case "powerStart":
        this.arpeggio([392, 523, 659, 784, 1046], 0.09, 0.1);
        break;
      case "powerEnd":
        this.sweep(784, 220, 0.4, "triangle", 0.12);
        break;
      case "gameOver":
        this.arpeggio([392, 311, 247, 196], 0.18, 0.14);
        break;
      case "gameOverRecord":
        this.arpeggio([523, 659, 784, 1046, 1318], 0.1, 0.1);
        break;
      case "button":
        this.tone(740, 0.06, 0.05);
        break;
      case "miss":
        this.tone(160, 0.12, 0.07);
        break;
      default:
        break;
    }
  }

  private applyGains(): void {
    if (this.musicGain) this.musicGain.gain.value = this.volumes.music * 0.55;
    if (this.ambientGain) this.ambientGain.gain.value = this.volumes.ambient * 0.35;
    if (this.sfxGain) this.sfxGain.gain.value = this.volumes.sfx * 0.5;
    if (this.musicOn) this.setPadLevel(0.04);
  }

  private setPadLevel(level: number): void {
    if (!this.padGain || !this.ctx) return;
    this.padGain.gain.setTargetAtTime(level * this.volumes.music, this.ctx.currentTime, 0.08);
  }

  private startPad(ctx: AudioContext): void {
    const dest = this.musicGain;
    if (!dest) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = 196;
    g.gain.value = 0;
    osc.connect(g);
    g.connect(dest);
    osc.start();
    this.pad = osc;
    this.padGain = g;
  }

  private startWaves(ctx: AudioContext): void {
    const length = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 280;
    filter.Q.value = 0.7;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.12;
    lfoGain.gain.value = 70;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    const ambient = this.ambientGain;
    if (!ambient) return;
    source.connect(filter);
    filter.connect(ambient);
    source.start();
    lfo.start();
    this.waveSource = source;
    this.waveLfo = lfo;
  }

  private scheduleMusic(): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicOn) return;
    const horizon = ctx.currentTime + 0.2;
    const stepDur = this.reduceMotion ? 0.42 : 0.26;
    while (this.nextNote < horizon) {
      this.playMusicStepAt(this.nextNote);
      this.nextNote += stepDur;
      this.step += 1;
    }
  }

  private playMusicStepAt(when: number): void {
    const melody = [523.25, 587.33, 659.25, 783.99, 880, 659.25, 587.33, 392];
    const bass = [130.81, 164.81, 146.83, 110];
    const note = melody[this.step % melody.length] ?? 523;
    const low = bass[Math.floor(this.step / 2) % bass.length] ?? 131;
    this.pluckAt(note, when, 0.28, 0.11);
    if (this.step % 2 === 0) this.pluckAt(low, when, 0.38, 0.1);
    if (this.step % 4 === 0) this.noiseAt(when, 0.05, 0.06, 1800);
    if (this.step % 8 === 4) this.pluckAt(note * 1.5, when, 0.18, 0.05);
  }

  private pluckAt(freq: number, when: number, duration: number, gain: number): void {
    const ctx = this.ctx;
    const dest = this.musicGain;
    if (!ctx || !dest) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    g.gain.setValueAtTime(gain, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    osc.connect(g);
    g.connect(dest);
    osc.start(when);
    osc.stop(when + duration);
  }

  private noiseAt(when: number, duration: number, gain: number, freq: number): void {
    const ctx = this.ctx;
    const dest = this.musicGain;
    if (!ctx || !dest) return;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    source.connect(filter);
    filter.connect(g);
    g.connect(dest);
    source.start(when);
  }

  private tone(freq: number, duration: number, gain: number, music = false): void {
    const ctx = this.ctx;
    const dest = music ? this.musicGain : this.sfxGain;
    if (!ctx || !dest) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const now = ctx.currentTime;
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(g);
    g.connect(dest);
    osc.start(now);
    osc.stop(now + duration);
  }

  private sweep(from: number, to: number, duration: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx;
    const dest = this.sfxGain;
    if (!ctx || !dest) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, to), now + duration);
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(g);
    g.connect(dest);
    osc.start(now);
    osc.stop(now + duration);
  }

  private arpeggio(notes: number[], gap: number, gain: number): void {
    notes.forEach((note, i) => {
      window.setTimeout(() => this.tone(note, 0.12, gain), i * gap * 1000);
    });
  }

  private noiseBurst(duration: number, gain: number, freq: number, music = false): void {
    const ctx = this.ctx;
    const dest = music ? this.musicGain : this.sfxGain;
    if (!ctx || !dest) return;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = freq;
    const g = ctx.createGain();
    const now = ctx.currentTime;
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter);
    filter.connect(g);
    g.connect(dest);
    source.start(now);
  }

  private chirp(): void {
    const ctx = this.ctx;
    const dest = this.ambientGain;
    if (!ctx || !dest) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    const now = ctx.currentTime;
    const start = 1400 + Math.random() * 600;
    osc.frequency.setValueAtTime(start, now);
    osc.frequency.exponentialRampToValueAtTime(start * 1.4, now + 0.12);
    osc.frequency.exponentialRampToValueAtTime(start * 0.9, now + 0.22);
    g.gain.setValueAtTime(0.05, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
    osc.connect(g);
    g.connect(dest);
    osc.start(now);
    osc.stop(now + 0.25);
  }
}
